import React, {
  Dispatch,
  Fragment,
  SetStateAction,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { navigate } from 'gatsby';
import {
  getCauses, getPreferredResolutions, getServiceGroups, hasOrchestration, isComplainable,
  isOrchestrationLineItemByRecipient,
} from '@printdeal/business-logic';
import { useFormik } from 'formik';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';
// @ts-expect-error
import { complaintSchema } from '@printdeal/validation';
import {
  Button,
  CheckboxGroup,
  Grid,
  Heading,
  Label,
  RadioGroup,
  TextField,
  TextareaField,
} from '@printdeal/ui-library';
import { useSelector } from 'react-redux';
import { ErrorManagement } from '@printdeal/error-management/react';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../../../store/slices/customerSlice';
import { messages } from '../../../translations/complaint';
import { ComplaintFormValuesProps, CreateComplaintPayload } from '../../../../../types/complaints';
import { getAttachmentDetails, shouldShowAttachmentField } from '../../../../../helpers/complaints-helper';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import useSystemPages from '../../../../../hooks/useSystemPages';
import LineItem from '../../../../../types/cart/LineItem';
import { OrderWithTotalDetails } from '../../../../../types/order/Order';
import useFormikValidation from '../../../../../hooks/useFormikValidation';
import { useOrders } from '../../hooks/useOrders';

import ComplaintPolicyExplanation from './ComplaintPolicyExplanation';

const ORDERS_PER_PAGE = 1;
interface ComplaintFormProps {
  order: OrderWithTotalDetails | undefined;
  hasError: boolean;
  setHasError: Dispatch<SetStateAction<boolean>>;
}

type FieldSetTypes = 'lineItems' | 'quantities' | 'causeOfComplaint' | 'description' | 'resolution';

const ComplaintForm = ({ order, hasError, setHasError }: ComplaintFormProps) => {
  const intl = useIntl();
  const customerGroupId: string = useSelector(customerSelectors.getCustomerGroupId);
  const resolutions = getPreferredResolutions();
  const causes = Object.values(getCauses());
  const orderLineItems: LineItem[] | undefined = order?.lineItems.filter((lineItem) => isComplainable(lineItem));
  const [accountDashboard] = useSystemPages('accountDashboard');

  const { orders } = useOrders({
    ordersPerPage: ORDERS_PER_PAGE,
    search: order?.id,
  });

  const complaintFormik = useFormik({
    initialValues: {
      attachments: [],
      lineItems: [],
      quantities: {},
      description: '',
      causeOfComplaint: '',
      resolution: '',
    },
    validationSchema: complaintSchema,
    validateOnBlur: true,
    onSubmit: async ({
      attachments, lineItems, quantities, description, causeOfComplaint, resolution,
    }: ComplaintFormValuesProps) => {
      let updatedLineItems = [...lineItems];
      const updatedQuantities = { ...quantities };
      const orchestrationLineItems = order?.lineItems.filter((lineItem) => hasOrchestration(lineItem));
      if (orchestrationLineItems && orchestrationLineItems.length > 0) {
        const filterLineItems = orders ? getServiceGroups(orders.flatMap((orderData) => orderData.lineItems)) : [];
        updatedLineItems = Object.values(filterLineItems)[0].map((item) => item.id);
        updatedLineItems.forEach((item) => {
          if (!quantities[item]) {
            const [firstQuantity] = Object.values(quantities);
            updatedQuantities[item] = firstQuantity;
          }
        });
      }

      if (hasError) {
        setHasError(false);
      }

      const payload = {
        orderId: order?.id,
        items: updatedLineItems?.map((itemId) => ({
          itemId,
          quantity: updatedQuantities[itemId],
        })),
        description,
        notes: [],
        attachments: attachments ? getAttachmentDetails(attachments) : [],
        preferredCustomerResolutions: [resolution],
        categories: [causeOfComplaint],
        customerGroupId,
      };

      try {
        const complaintService = ServiceFactory.createComplaintsService();
        await complaintService.createComplaint(payload as CreateComplaintPayload);
        // eslint-disable-next-line @typescript-eslint/return-await
        return await navigate(`${accountDashboard.path}/complaints`, {
          state: {
            feedback: {
              kind: 'success',
              message: intl.formatMessage(messages.success),
            },
          },
        });
      } catch (error) {
        ErrorManagement.exception({
          exception: error,
          extra: {
            orderId: payload.orderId,
            lineItems: JSON.stringify(payload.items),
            resolution,
            causeOfComplaint,
          },
          tags: {
            fileLocation: 'ComplaintForm',
            featureFunction: 'addComplaint',
          },
          fingerprint: ['complaints-add-complaint'],
        });

        setHasError(true);

        return null;
      }
    },
  });

  const {
    handleSubmit,
    errors,
    touched,
    getFieldProps,
    isSubmitting,
    isValid,
    handleChange,
    values,
    handleBlur,
    setFieldValue,
    setTouched,
    submitCount,
  } = complaintFormik;

  const handleAttachment = (uploads: IAlternativeUpload[]) => {
    if (uploads) {
      setFieldValue('attachments', uploads);
    }
  };

  const validQuantity = (selected: number, maxQuantity: number, lineItemId: string) => {
    const isQuantitySelected = Boolean(selected <= maxQuantity && selected > 0);
    if (touched.quantities?.[lineItemId]
        || submitCount > 0) {
      return isQuantitySelected ? 'success' : 'error';
    }
    return undefined;
  };

  const { validation } = useFormikValidation({
    values,
    errors,
    touched,
  });

  const validationErrorMessage = (fieldSet: FieldSetTypes) => {
    if (validation[fieldSet] === 'error') {
      return intl.formatMessage(messages[`${fieldSet}ValidationError`]);
    }

    return undefined;
  };

  return (
    <form
      onSubmit={handleSubmit}
      method="POST"
      className="pd-mb-5"
    >
      <Grid className="pd-mt-5 pd-gap-5">
        <Heading size="2">
          <FormattedMessage
            id="complaint.form.complaintDetails.title"
            defaultMessage="2. Complete complaints details"
          />
        </Heading>

        <CheckboxGroup
          id="lineitems-checkbox-group"
          name="lineItems"
          label={intl.formatMessage({
            id: 'complaint.form.lineItems.title',
            defaultMessage: 'Line items',
          })}
          required
          requiredLabel={intl.formatMessage({
            id: 'general.form.requiredLabel',
            defaultMessage: 'Required field',
          })}
          className="pd-max-w-lg"
          validation={validation.lineItems}
          validationMessage={validationErrorMessage('lineItems')}
        >
          {orderLineItems?.map((lineItem) => (
            <Fragment key={lineItem.id}>
              <CheckboxGroup.Field
                id={`lineItem-${lineItem.id}`}
                key={`lineItem-${lineItem.id}`}
                label={(
                  <FormattedMessage
                    id="complaint.form.lineItem.title"
                    defaultMessage="{title}, {quantity}"
                    values={{
                      title: lineItem?.custom?.fields?.merchandisingProductName,
                      quantity: (
                        <FormattedMessage
                          id="account.order.orderline.quantity"
                          defaultMessage="{quantity} {quantity, plural, one {piece} other {pieces}}"
                          values={{ quantity: lineItem.quantity }}
                        />
                      ),
                    }}
                  />
                )}
                value={lineItem.id}
                description={!isOrchestrationLineItemByRecipient(lineItem, 'customer') ? lineItem?.custom?.fields?.lineItemNumber : ''}
                onBlur={handleBlur}
                onChange={(event) => {
                  if (values.quantities?.[lineItem.id] && !event.target.checked) {
                    const quantities = { ...values.quantities };
                    delete quantities[lineItem.id];
                    setFieldValue('quantities', { ...quantities });
                    setTouched({ [`quantities.${lineItem.id}`]: false });
                  }
                  handleChange(event);
                }}
              />

              {values.lineItems?.includes(lineItem.id) && (
                <TextField
                  id={`quantities.${lineItem.id}`}
                  label={intl.formatMessage({
                    id: 'complaint.form.itemList.concernQuantity',
                    defaultMessage: 'Complaint concerns*',
                  })}
                  type="number"
                  defaultValue="0"
                  name={`quantities.${lineItem.id}`}
                  suffix={intl.formatMessage({
                    id: 'account.order.orderline.quantity',
                    defaultMessage: '{quantity} {quantity, plural, one {piece} other {pieces}}',
                  }, {
                    quantity: lineItem.quantity,
                  })}
                  className="pd-ml-20 pd-max-w-xs"
                  validation={validQuantity(values.quantities?.[lineItem.id], lineItem.quantity, lineItem.id)}
                  validationMessage={validationErrorMessage('quantities')}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue('quantities', {
                      ...values.quantities,
                      [lineItem.id]: parseInt(event.target.value, 10),
                    });
                  }}
                  min="1"
                  max={lineItem.quantity}
                  value={values.quantities?.[lineItem.id]}
                  data-cy={`lineItemQuantity-${lineItem.id}`}
                  required
                  requiredLabel={intl.formatMessage({
                    id: 'general.form.requiredLabel',
                    defaultMessage: 'Required field',
                  })}
                />
              )}
            </Fragment>
          ))}
        </CheckboxGroup>

        <RadioGroup
          name="causeOfComplaint"
          id="cause-of-complaint"
          label={intl.formatMessage({
            id: 'complaint.form.causeOfComplaint.title',
            defaultMessage: 'Cause of the complaint*',
          })}
          className="pd-max-w-lg"
          validation={validation.causeOfComplaint}
          validationMessage={validationErrorMessage('causeOfComplaint')}
          required
          requiredLabel={intl.formatMessage({
            id: 'general.form.requiredLabel',
            defaultMessage: 'Required field',
          })}
        >
          {causes.map((cause) => (
            <RadioGroup.Field
              id={`cause-${cause.name}`}
              key={`cause-${cause.name}`}
              value={cause.mappedMcpKey}
              label={intl.formatMessage(messages[cause.translationKey.split('.')[2]])}
              checked={values.causeOfComplaint === cause.mappedMcpKey}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          ))}
        </RadioGroup>

        <TextareaField
          id="description"
          label={intl.formatMessage({
            id: 'complaint.form.description',
            defaultMessage: 'Describe your complaint*',
          })}
          className="pd-max-w-lg"
          validation={validation.description}
          validationMessage={validationErrorMessage('description')}
          required
          requiredLabel={intl.formatMessage({
            id: 'general.form.requiredLabel',
            defaultMessage: 'Required field',
          })}
          {...getFieldProps('description')}
        />

        <RadioGroup
          name="resolution"
          id="complaint-resolution"
          label={intl.formatMessage({
            id: 'complaint.form.resolution',
            defaultMessage: 'Desired solution*',
          })}
          className="pd-max-w-lg"
          validation={validation.resolution}
          validationMessage={validationErrorMessage('resolution')}
          required
          requiredLabel={intl.formatMessage({
            id: 'general.form.requiredLabel',
            defaultMessage: 'Required field',
          })}
        >
          {resolutions.map((resolution) => (
            <RadioGroup.Field
              checked={values.resolution === resolution.mappedMcpKey}
              id={`resolution-${resolution.name}`}
              key={`resolution-${resolution.name}`}
              value={resolution.mappedMcpKey}
              label={intl.formatMessage(messages[resolution.translationKey.split('.')[2]])}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          ))}
        </RadioGroup>

        {
          shouldShowAttachmentField(complaintFormik.values.causeOfComplaint) && (
            <fieldset className="pd-mb-4">
              <Label className="pd-mb-2">
                <FormattedMessage id="complaint.form.uploadAttachments" defaultMessage="Photos or other files" /> <span className="pd-text-error-500">*</span>
              </Label>
              <p id="text-input-description" className="pd-mb-2 pd-text-sm pd-text-dim-500">
                <FormattedMessage id="complaint.form.uploadAttachments.description" defaultMessage="Take pictures of the product the complaint is about and upload them here." />
              </p>
              <div className="pd-rounded pd-border pd-p-4">
                <AlternativeUpload
                  id="complaint-form"
                  name="complaint-form"
                  onChange={handleAttachment}
                  dataLayerData={{ location: 'complaint_form' }}
                />
              </div>
              {errors.attachments && (touched.attachments || submitCount > 0) && (
                <span className="pd-text-sm pd-text-error-600">
                  <FormattedMessage id="complaint.form.error.attachments" defaultMessage="Please provide one or more attachments." />
                </span>
              )}
            </fieldset>
          )
        }

        <div className="pd-text-right">
          <Button
            type="submit"
            data-cy="buttonSubmitComplaint"
            disabled={isSubmitting || !isValid}
            isLoading={isSubmitting}
          >
            <FormattedMessage id="complaint.submit.button" defaultMessage="Submit Complaint" />
          </Button>
        </div>

        <ComplaintPolicyExplanation />
      </Grid>
    </form>
  );
};

export default ComplaintForm;
